import { useState, type ReactNode } from "react";
import { TgScreen } from "../../components/TgScreen";
import { Spinner } from "../../components/atoms";
import { useWallet } from "../context";
import { CreateSheet } from "./CreateSheet";
import { ImportSheet } from "./ImportSheet";
import { RestoreSheet } from "./RestoreSheet";
import { WalletSetupSheet } from "./WalletSetupSheet";

type Mode = null | "setup" | "create" | "import";

/**
 * Top-level gate that decides what wallet UI (if any) to show
 * before letting the rest of the app render.
 *
 *  - boot          → spinner
 *  - unlocked      → children (app proceeds)
 *  - none          → WalletSetupSheet (Create / Import / Skip)
 *  - needs-restore → RestoreSheet
 *  - error         → error card with Reset button
 *
 * If the user picks "Skip", we render children anyway — the rest of the
 * app gracefully degrades when there's no wallet (manual destination,
 * no Send-from-wallet button).
 */
export function WalletGate({ children }: { children: ReactNode }) {
  const { state, reset } = useWallet();
  const [mode, setMode] = useState<Mode>(null);
  const [skipped, setSkipped] = useState(false);

  if (state.kind === "boot") {
    return (
      <TgScreen>
        <div
          style={{
            minHeight: 500,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: "var(--hint)",
          }}
        >
          <Spinner size={24} />
        </div>
      </TgScreen>
    );
  }

  if (state.kind === "error") {
    return (
      <TgScreen
        title="Wallet error"
        mainButton={{ text: "Reset wallet", destructive: true, onClick: () => void reset() }}
      >
        <div style={{ padding: "40px 24px", textAlign: "center" }}>
          <div style={{ fontSize: 14, color: "var(--danger)", marginBottom: 10 }}>
            {state.message}
          </div>
          <div style={{ fontSize: 12, color: "var(--hint)" }}>
            Resetting clears the stored wallet. You can re-import with your mnemonic afterwards.
          </div>
        </div>
      </TgScreen>
    );
  }

  if (state.kind === "needs-restore") {
    return <RestoreSheet reason={state.reason} onDone={() => setMode(null)} />;
  }

  if (state.kind === "none") {
    if (skipped) return <>{children}</>;
    if (mode === "create") return <CreateSheet onBack={() => setMode("setup")} onDone={() => setMode(null)} />;
    if (mode === "import") return <ImportSheet onBack={() => setMode("setup")} onDone={() => setMode(null)} />;
    return (
      <WalletSetupSheet
        onCreate={() => setMode("create")}
        onImport={() => setMode("import")}
        onSkip={() => setSkipped(true)}
      />
    );
  }

  // unlocked
  return <>{children}</>;
}
